import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { tasksApi, boardsApi } from '../../api/client'
import BoardColumn from './BoardColumn'
import TaskCard from './TaskCard'
import CreateColumnModal from './CreateColumnModal'
import CreateTaskModal from './CreateTaskModal'
import { HiPlus } from 'react-icons/hi'

export default function BoardView({ board, onUpdate }) {
  const [columns, setColumns] = useState([])
  const [activeTask, setActiveTask] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showCreateColumn, setShowCreateColumn] = useState(false)
  const [showCreateTask, setShowCreateTask] = useState(null) // columnId or null
  const columnsRef = useRef(columns)
  columnsRef.current = columns

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const fetchBoardData = useCallback(async () => {
    if (!board?.id) return
    try {
      setLoading(true)
      const response = await tasksApi.getBoardTasks(board.id)
      // API returns { board, columns } - extract columns array
      const data = Array.isArray(response.data?.columns) ? response.data.columns : []
      setColumns(data.map(col => ({ ...col, tasks: Array.isArray(col.tasks) ? col.tasks : [] })))
    } catch (error) {
      console.error('Failed to fetch board data:', error)
    } finally {
      setLoading(false)
    }
  }, [board?.id])

  useEffect(() => {
    fetchBoardData()
  }, [fetchBoardData])

  const findColumnByTaskId = (cols, taskId) => {
    for (const column of cols) {
      if (column.tasks?.some(t => t.id === taskId)) {
        return column
      }
    }
    return null
  }

  const handleDragStart = (event) => {
    const { active } = event
    const column = findColumnByTaskId(columnsRef.current, active.id)
    if (column) {
      const task = column.tasks.find(t => t.id === active.id)
      setActiveTask(task)
    }
  }

  const handleDragOver = (event) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id
    const overId = over.id

    // Use functional update to avoid stale closure issues
    setColumns(prev => {
      const activeColumn = findColumnByTaskId(prev, activeId)
      if (!activeColumn) return prev

      // Determine target column: either the column containing the over-task, or the column itself
      let overColumn = findColumnByTaskId(prev, overId)
      if (!overColumn) {
        overColumn = prev.find(c => c.id === overId)
      }
      if (!overColumn) return prev

      // Same column — let SortableContext handle reordering within column
      if (activeColumn.id === overColumn.id) return prev

      // Cross-column move
      const task = activeColumn.tasks.find(t => t.id === activeId)
      if (!task) return prev

      const overIndex = overId === overColumn.id
        ? overColumn.tasks.length
        : overColumn.tasks.findIndex(t => t.id === overId)

      return prev.map(col => {
        if (col.id === activeColumn.id) {
          return { ...col, tasks: col.tasks.filter(t => t.id !== activeId) }
        }
        if (col.id === overColumn.id) {
          const newTasks = [...col.tasks]
          const insertAt = overIndex < 0 ? newTasks.length : overIndex
          newTasks.splice(insertAt, 0, task)
          return { ...col, tasks: newTasks }
        }
        return col
      })
    })
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event
    setActiveTask(null)

    if (!over) return

    const activeId = active.id
    const overId = over.id

    // Use the ref for current state to get accurate column after all drag-over updates
    const currentCols = columnsRef.current
    const column = findColumnByTaskId(currentCols, activeId)

    if (!column) return

    // Handle within-column reorder
    const overColumn = findColumnByTaskId(currentCols, overId) || currentCols.find(c => c.id === overId)
    if (overColumn && column.id === overColumn.id && activeId !== overId) {
      const oldIndex = column.tasks.findIndex(t => t.id === activeId)
      const newIndex = column.tasks.findIndex(t => t.id === overId)
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setColumns(prev => prev.map(col => {
          if (col.id === column.id) {
            return { ...col, tasks: arrayMove(col.tasks, oldIndex, newIndex) }
          }
          return col
        }))
      }
    }

    // Find the final position in the column
    const finalColumn = findColumnByTaskId(columnsRef.current, activeId)
    if (!finalColumn) return
    const taskIndex = finalColumn.tasks.findIndex(t => t.id === activeId)

    try {
      await tasksApi.move(activeId, finalColumn.id, taskIndex)
      if (onUpdate) onUpdate()
    } catch (error) {
      console.error('Failed to move task:', error)
      // Revert on error
      fetchBoardData()
    }
  }

  const handleCreateColumn = async (data) => {
    try {
      await boardsApi.createColumn(board.id, data)
      setShowCreateColumn(false)
      fetchBoardData()
    } catch (error) {
      console.error('Failed to create column:', error)
    }
  }

  const handleDeleteColumn = async (columnId) => {
    if (!window.confirm('Are you sure you want to delete this column? All tasks will be moved to the first column.')) {
      return
    }
    try {
      await boardsApi.deleteColumn(columnId)
      fetchBoardData()
    } catch (error) {
      console.error('Failed to delete column:', error)
    }
  }

  const handleCreateTask = async (data) => {
    try {
      await tasksApi.create({
        ...data,
        project_id: board.project_id,
        board_id: board.id,
        column_id: showCreateTask,
      })
      setShowCreateTask(null)
      fetchBoardData()
    } catch (error) {
      console.error('Failed to create task:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  return (
    <div className="h-full">
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 h-full overflow-x-auto pb-4">
          {columns.map(column => (
            <BoardColumn
              key={column.id}
              column={column}
              onAddTask={() => setShowCreateTask(column.id)}
              onDelete={() => handleDeleteColumn(column.id)}
            />
          ))}
          {/* Add Column Button */}
          <div className="shrink-0 w-72">
            <button
              onClick={() => setShowCreateColumn(true)}
              className="w-full h-12 border-2 border-dashed border-surface-300 dark:border-surface-600 rounded-lg flex items-center justify-center gap-2 text-surface-500 dark:text-surface-400 hover:border-brand-400 hover:text-brand-600 transition-colors"
            >
              <HiPlus className="w-5 h-5" />
              Add Column
            </button>
          </div>
        </div>

        <DragOverlay>
          {activeTask && (
            <TaskCard task={activeTask} isDragging />
          )}
        </DragOverlay>
      </DndContext>

      {/* Create Column Modal */}
      {showCreateColumn && (
        <CreateColumnModal
          onClose={() => setShowCreateColumn(false)}
          onCreate={handleCreateColumn}
          existingCount={columns.length}
        />
      )}

      {/* Create Task Modal */}
      {showCreateTask && (
        <CreateTaskModal
          onClose={() => setShowCreateTask(null)}
          onCreate={handleCreateTask}
        />
      )}
    </div>
  )
}
